import { useState } from 'react'
import useTranslation from '../hooks/useTranslation'
import FullHeightContainer from '../UI/FullHeightContainer/FullHeightContainer'
import Container from '../UI/Container/Container'
import SlideInView from '../UI/SlideInView/SlideInView'
import FloatingHeadline from '../UI/FloatingHeadline/FloatingHeadline'
import Headline from './ui/Headline'
import chevronRight from '../../assets/image/highlights/chevron-right.png'
import chevronLeft from '../../assets/image/highlights/chevron-left.png'
import mpsarchonicImage from '../../assets/image/highlights/projects/mps_stuttgart.webp'
import mpsbietigheimImage from '../../assets/image/highlights/projects/mps_bietigheim.webp'
import roeslerImage from '../../assets/image/highlights/projects/roesler.webp'
import freudenbergImage from '../../assets/image/highlights/projects/freudenberg.webp'
import enoplanImage from '../../assets/image/highlights/projects/enoplan.webp'
import witzenmannImage from '../../assets/image/highlights/projects/witzenmann.webp'
import wolffMuellerImage from '../../assets/image/highlights/projects/wolff_mueller.webp'
import Button from '../UI/Button/Button'

function HighlightsSection() {
  const t = useTranslation()
  const { kicker, headline, projects, nextImage, moreInformation } = t.landing.highlights
  const [currentIndex, setCurrentIndex] = useState(0)
  const project = projects[currentIndex]
  const goNext = () => setCurrentIndex((i) => Math.min(i + 1, projects.length - 1))
  const goPrevious = () => setCurrentIndex((i) => Math.max(i - 1, 0))
  const goTo = (index: number) => setCurrentIndex(index)
  const images = [mpsarchonicImage, roeslerImage, freudenbergImage, enoplanImage, witzenmannImage, mpsbietigheimImage, wolffMuellerImage]

  return (
    <FullHeightContainer center id="projekte">
      <div>
        <Container>
          <SlideInView>
            <div>
              <FloatingHeadline text={kicker} />
              <Headline headline={headline} size="xlarge" className="mt-7" />
              <div className="relative mx-auto max-w-full aspect-video overflow-hidden mt-5">
                <div className="relative aspect-video w-full overflow-hidden bg-lightGray">
                  {projects.map((p, i) => (
                    <div
                      key={p.name}
                      className="absolute inset-0 transition-opacity duration-300"
                      style={{ opacity: i === currentIndex ? 1 : 0, pointerEvents: i === currentIndex ? 'auto' : 'none' }}
                    >
                      <img
                        src={images[i]}
                        alt=""
                        className="h-full w-full object-cover object-bottom"
                        onError={(e) => {
                          const target = e.currentTarget
                          target.style.display = 'none'
                          const placeholder = target.nextElementSibling as HTMLElement
                          if (placeholder) placeholder.style.display = 'flex'
                        }}
                      />
                      <div
                        className="flex h-full w-full items-center justify-center bg-superLightGray text-blue/60"
                        style={{ display: p.image ? 'none' : 'flex' }}
                      >
                        <span className="text-lg font-semibold">{p.name}</span>
                      </div>
                    </div>
                  ))}
                  {currentIndex > 0 ? (
                    <button
                      type="button"
                      onClick={goPrevious}
                      className="absolute left-2 top-1/2 flex -translate-y-1/2 items-center justify-center md:left-4 md:h-14 md:w-14"
                      aria-label="Previous image"
                    >
                      <img src={chevronLeft} alt="Chevron Left" className="w-6 md:w-8" />
                    </button>
                  ) : null}
                  {currentIndex < projects.length - 1 ? (
                    <button
                      type="button"
                      onClick={goNext}
                      className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center justify-center md:right-4 md:h-14 md:w-14"
                      aria-label={nextImage}
                    >
                      <img src={chevronRight} alt="Chevron Right" className="w-6 md:w-8" />
                    </button>
                  ) : null}
                </div>

                {/* Pagination dashes below image */}
                <div className="flex justify-center gap-2 absolute bottom-5 md:bottom-10 left-0 right-0">
                  {projects.map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => goTo(i)}
                      className={`${i === currentIndex ? 'h-[4px]' : 'h-[2px] mt-[1px]'} min-w-6 md:min-w-8 flex-1 max-w-12 md:max-w-16 transition-colors bg-white`}
                      aria-label={`Slide ${i + 1}`}
                    />
                  ))}
                </div>
              </div>
              {/* Text block below slider – changes with slide */}
              <div className="mt-6 text-left">
                <h3 className="text-xl font-semibold text-blue md:text-2xl">{project.name}</h3>
                <div className="flex gap-0 flex-col text-sm text-blue md:text-base">
                  <div>{project.area}</div>
                  <div>{project.duration}</div>
                  {project.link && (
                    <Button
                      href={project.link}
                      newTab
                      text={moreInformation}
                      small
                      className="bg-blue rounded-none w-fit border-blue hover:bg-white hover:text-blue mt-1"
                    />
                  )}
                </div>
              </div>
            </div>
          </SlideInView>
        </Container>
      </div>
    </FullHeightContainer>
  )
}

export default HighlightsSection
